import { THEMES, setCurrentTheme, getCurrentTheme } from '../config/themes.config';

export class ThemeSelector {
    private container: HTMLDivElement;
    private isOpen: boolean = false;
    
    constructor() {
        this.container = document.createElement('div');
        this.container.id = 'theme-selector';
        this.setupStyles();
        this.createUI();
        document.body.appendChild(this.container);
    }
    
    public static shouldShow(): boolean {
        // Only show theme selector in slot mode (GameScene)
        // Check current scene or URL to determine game mode
        return window.location.hash !== '#videopoker' && 
               !window.location.pathname.includes('videopoker');
    }
    
    private setupStyles(): void {
        const style = document.createElement('style');
        style.textContent = `
            #theme-selector {
                position: fixed;
                top: 10px;
                right: 10px;
                z-index: 1000;
                font-family: 'Orbitron', Arial, sans-serif;
            }
            
            .theme-button {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                border: 2px solid #00FFFF;
                padding: 10px 20px;
                border-radius: 25px;
                cursor: pointer;
                font-size: 14px;
                font-weight: bold;
                text-transform: uppercase;
                transition: all 0.3s ease;
                box-shadow: 0 4px 15px rgba(0, 255, 255, 0.3);
            }
            
            .theme-button:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(0, 255, 255, 0.5);
                background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
            }
            
            .theme-menu {
                display: none;
                position: absolute;
                top: 50px;
                right: 0;
                background: rgba(0, 0, 0, 0.95);
                border: 2px solid #00FFFF;
                border-radius: 10px;
                padding: 10px;
                min-width: 250px;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
            }
            
            .theme-menu.open {
                display: block;
                animation: slideDown 0.3s ease;
            }
            
            @keyframes slideDown {
                from {
                    opacity: 0;
                    transform: translateY(-10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            .theme-item {
                padding: 12px 15px;
                margin: 5px 0;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid transparent;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.3s ease;
            }
            
            .theme-item:hover {
                background: rgba(255, 255, 255, 0.2);
                border-color: #00FFFF;
                transform: translateX(5px);
            }
            
            .theme-item.active {
                background: rgba(0, 255, 255, 0.2);
                border-color: #00FFFF;
            }
            
            .theme-name {
                color: #00FFFF;
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 4px;
            }
            
            .theme-description {
                color: #FFFFFF;
                font-size: 12px;
                opacity: 0.8;
            }
            
            .theme-preview {
                display: inline-block;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                margin-right: 8px;
                vertical-align: middle;
            }
        `;
        document.head.appendChild(style);
    }
    
    private createUI(): void {
        const currentTheme = getCurrentTheme();
        
        // Create main button
        const button = document.createElement('button');
        button.className = 'theme-button';
        button.innerHTML = `🎨 ${currentTheme.name}`;
        button.onclick = () => this.toggleMenu();
        
        // Create dropdown menu
        const menu = document.createElement('div');
        menu.className = 'theme-menu';
        
        // Add theme options
        Object.values(THEMES).forEach(theme => {
            const item = document.createElement('div');
            item.className = 'theme-item';
            if (theme.id === currentTheme.id) {
                item.classList.add('active');
            }
            
            // Create color preview
            const preview = document.createElement('span');
            preview.className = 'theme-preview';
            preview.style.background = `linear-gradient(135deg, #${theme.colors.uiAccent1.toString(16)}, #${theme.colors.uiAccent2.toString(16)})`;
            
            const nameDiv = document.createElement('div');
            nameDiv.className = 'theme-name';
            nameDiv.appendChild(preview);
            nameDiv.appendChild(document.createTextNode(theme.name));
            
            const descDiv = document.createElement('div');
            descDiv.className = 'theme-description';
            descDiv.textContent = theme.description;
            
            item.appendChild(nameDiv);
            item.appendChild(descDiv);
            
            item.onclick = () => this.selectTheme(theme.id);
            menu.appendChild(item);
        });
        
        this.container.appendChild(button);
        this.container.appendChild(menu);
    }
    
    private toggleMenu(): void {
        this.isOpen = !this.isOpen;
        const menu = this.container.querySelector('.theme-menu');
        if (menu) {
            if (this.isOpen) {
                menu.classList.add('open');
            } else {
                menu.classList.remove('open');
            }
        }
    }
    
    private selectTheme(themeId: string): void {
        setCurrentTheme(themeId);
        // Reload the page to apply new theme
        window.location.reload();
    }
    
    public destroy(): void {
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}